import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { CButton } from "@coreui/react";
import DashboardContent from "../../components/DashboardContent";
import DataMappingSingleSelect from "../../components/DataMappingSingleSelect";
import DataMappingMultiSelect from "../../components/DataMappingMultiSelect";
import Chip from "../../components/Chip";
import StatusPill from "../../components/StatusPill";
import Loader from "../../utils/Loader";
import usePatientNarrativeWizardSection from "../../hooks/usePatientNarrativeWizardSection";
import { usePermissions } from "../../contexts/PermissionsContext";
import { NO_PERMISSION_TITLE, PERMISSIONS } from "../../utils/permissions";
import { PATIENT_NARRATIVE_WIZARD_SECTIONS } from "../../utils/patientNarrativeWizardSections";
import { CsrAPI } from "../../apis/CsrAPI";
import {
  PN_DATA_MAPPING_DEMO,
  SOURCE_LISTING_OPTIONS,
  CIOMS_SOURCE_OPTIONS,
  CIOMS_CASE_OPTIONS,
  CIOMS_CASE_METADATA,
  DEMO_LINKED_CSR_LABEL,
} from "./dataMappingDemoData";

// How long the simulated Auto-Map "processing" takes. Short enough to stay
// snappy in a live demo, long enough to read as real work happening.
const AUTO_MAP_DELAY_MS = 700;

const SOURCE_LISTING_FIELDS = [
  {
    key: "severity",
    label: "Adverse events with severity",
    required: true,
    placeholder: "Select Adverse Events with Severity data tables...",
  },
  {
    key: "narrativeInterestCategory",
    label: "Adverse events by narrative interest category",
    required: true,
    placeholder:
      "Select Adverse Events by Narrative Interest Category data tables...",
  },
  {
    key: "exposure",
    label: "Exposure and dosing",
    required: false,
    placeholder: "Select Exposure and Dosing data tables...",
  },
  {
    key: "medicalHistory",
    label: "Medical history",
    required: false,
    placeholder: "Select Medical History data tables...",
  },
  {
    key: "concomitantMedications",
    label: "Concomitant medications",
    required: false,
    placeholder: "Select Concomitant Medications data tables...",
  },
];

const DEFAULT_SOURCE_LISTINGS = {
  severity: null,
  narrativeInterestCategory: null,
  exposure: null,
  medicalHistory: null,
  concomitantMedications: null,
};

const TERMINOLOGY_LABELS = {
  patients: "Patients",
  subjects: "Subjects",
  participants: "Participants",
};

const sessionKeyFor = patientNarrativeId =>
  `pn-data-mapping-demo:${patientNarrativeId}`;

// Builds a react-select-async-compatible loadOptions function from a plain
// string array - lets the demo dropdowns reuse DataMappingSingleSelect as-is.
const loadOptionsFrom = values => async inputValue => {
  const options = values.map(value => ({ value, label: value }));
  if (!inputValue) return options;
  return options.filter(option =>
    option.label.toLowerCase().includes(inputValue.toLowerCase())
  );
};

const DataMapping = () => {
  // Route param is named studyId (see AppRoutes.js), but for the PN app
  // this is a patient_narrative_id, not a Study id.
  const { studyId: patientNarrativeId } = useParams();
  const { hasPermission } = usePermissions();
  const sessionKey = sessionKeyFor(patientNarrativeId);

  // pn:wizards:read gates the Project & Conventions GET this page reads its
  // terminology from; changing the mapping - Save, and the Auto-Map that
  // proposes one - needs pn:wizards:update on top of it.
  const canReadWizards = hasPermission(PERMISSIONS.pnWizardsRead);
  const canSaveWizards =
    canReadWizards && hasPermission(PERMISSIONS.pnWizardsUpdate);

  // Demo-only architecture: there is no Data Mapping backend for Patient
  // Narrative yet, so this whole page's mapping state is frontend-only.
  // sessionStorage (scoped to this patientNarrativeId) is used purely so
  // Save "feels" persistent across a page navigation during a live demo -
  // it is not a substitute for a real API and is lost on tab close.
  const [initialDemoState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(sessionKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [hasAutoMapped, setHasAutoMapped] = useState(
    !!initialDemoState?.hasAutoMapped
  );
  const [isAutoMapping, setIsAutoMapping] = useState(false);
  const [sourceListings, setSourceListings] = useState(
    initialDemoState?.sourceListings || DEFAULT_SOURCE_LISTINGS
  );
  // A PN can pull cases from more than one CIOMS source file (e.g. an
  // initial line listing plus a separately-delivered follow-up listing),
  // so this is multi-select - array, not a single filename.
  const [ciomsFiles, setCiomsFiles] = useState(
    initialDemoState?.ciomsFiles || []
  );
  const [ciomsMeta, setCiomsMeta] = useState(
    initialDemoState?.ciomsMeta || null
  );
  const [mappings, setMappings] = useState(initialDemoState?.mappings || []);
  const [patientIdInput, setPatientIdInput] = useState("");
  const [linkedCsrId, setLinkedCsrId] = useState(null);

  const autoMapTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (autoMapTimeoutRef.current) clearTimeout(autoMapTimeoutRef.current);
    };
  }, []);

  // Non-blocking: this is a real read of the PN's own linked_csr_id, used
  // only to decide whether to show "from FD-7931 CSR project" - the demo
  // works fine without it if the request fails.
  useEffect(() => {
    if (!patientNarrativeId) return undefined;
    let cancelled = false;
    CsrAPI.getPatientNarrative({ patientNarrativeId })
      .then(data => {
        if (!cancelled) setLinkedCsrId(data?.linked_csr_id || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [patientNarrativeId]);

  // Also a real (non-blocking) read - only used to phrase "Patients
  // identified" using the project's configured terminology, with a
  // sensible fallback if it hasn't been answered yet.
  const { answers: conventionAnswers } = usePatientNarrativeWizardSection(
    patientNarrativeId,
    PATIENT_NARRATIVE_WIZARD_SECTIONS.projectConventions,
    { canRead: canReadWizards, canSave: canSaveWizards }
  );
  const subjectsLabel =
    TERMINOLOGY_LABELS[conventionAnswers?.qParticipantTerm] || "Patients";

  const totalPatients = mappings.length;
  const fromListings = mappings.filter(row => !row.manual).length;
  const addedManually = mappings.filter(row => row.manual).length;
  // "N CIOMS cases matched" reports the CIOMS source's total case count (as
  // of the last Auto-Map), not a live count of "Matched" status rows - a
  // single CIOMS source can contain more cases than currently-matched
  // patients (e.g. follow-up cases folded into one patient's row).
  const matchedCaseCount = ciomsMeta?.caseCount ?? 0;

  const handleAutoMap = () => {
    if (!canSaveWizards) return;

    setIsAutoMapping(true);
    autoMapTimeoutRef.current = setTimeout(() => {
      setSourceListings({ ...PN_DATA_MAPPING_DEMO.sourceListings });
      setCiomsFiles([PN_DATA_MAPPING_DEMO.cioms.filename]);
      setCiomsMeta({
        caseCount: PN_DATA_MAPPING_DEMO.cioms.caseCount,
        subjectCount: PN_DATA_MAPPING_DEMO.cioms.subjectCount,
        identifierField: PN_DATA_MAPPING_DEMO.cioms.identifierField,
        followUpPolicy: PN_DATA_MAPPING_DEMO.cioms.followUpPolicy,
      });
      setMappings(prev => {
        const demoRows = PN_DATA_MAPPING_DEMO.mappings.map(row => ({
          ...row,
          ciomsCases: row.ciomsCases.map(c => ({ ...c })),
          manual: false,
        }));
        const demoIds = new Set(demoRows.map(row => row.patientId));
        const keptManualRows = prev.filter(
          row => row.manual && !demoIds.has(row.patientId)
        );
        return [...demoRows, ...keptManualRows];
      });
      setHasAutoMapped(true);
      setIsAutoMapping(false);
    }, AUTO_MAP_DELAY_MS);
  };

  const handleAddPatient = () => {
    const trimmed = patientIdInput.trim();
    if (!trimmed) return;
    if (mappings.some(row => row.patientId === trimmed)) {
      toast.info("That patient ID has already been added.");
      return;
    }
    setMappings(prev => [
      ...prev,
      {
        patientId: trimmed,
        reasons: ["Manually added"],
        listingEvents: 0,
        ciomsCases: [],
        status: "unmatched",
        manual: true,
      },
    ]);
    setPatientIdInput("");
  };

  const handleRemovePatient = patientId => {
    setMappings(prev =>
      prev.filter(row => !(row.patientId === patientId && row.manual))
    );
  };

  const handleUploadListClick = () => {
    fileInputRef.current?.click();
  };

  // No CSV parsing here by design - see module docstring in
  // dataMappingDemoData.js. This only acknowledges the file was picked.
  const handleUploadListChange = event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    toast.info(
      `${file.name} selected - patient ID extraction isn't implemented in this demo yet.`
    );
  };

  const handleSelectCiomsCase = (patientId, caseId) => {
    if (!caseId) return;
    const meta = CIOMS_CASE_METADATA[caseId] || {
      eventCount: 1,
      followUpLabel: null,
    };
    setMappings(prev =>
      prev.map(row =>
        row.patientId === patientId
          ? {
              ...row,
              ciomsCases: [
                {
                  caseId,
                  eventCount: meta.eventCount,
                  followUpLabel: meta.followUpLabel,
                },
              ],
              status: "matched",
            }
          : row
      )
    );
  };

  const handleAddCiomsCase = patientId => {
    setMappings(prev =>
      prev.map(row => {
        if (row.patientId !== patientId) return row;
        const used = new Set(row.ciomsCases.map(c => c.caseId));
        const nextCaseId = CIOMS_CASE_OPTIONS.find(id => !used.has(id));
        if (!nextCaseId) return row;
        const meta = CIOMS_CASE_METADATA[nextCaseId];
        return {
          ...row,
          ciomsCases: [
            ...row.ciomsCases,
            {
              caseId: nextCaseId,
              eventCount: meta.eventCount,
              followUpLabel: meta.followUpLabel,
            },
          ],
          status: "matched",
        };
      })
    );
  };

  const handleSave = event => {
    event?.preventDefault?.();
    if (!canSaveWizards) return { success: false };
    try {
      sessionStorage.setItem(
        sessionKey,
        JSON.stringify({
          hasAutoMapped,
          sourceListings,
          ciomsFiles,
          ciomsMeta,
          mappings,
        })
      );
      return { success: true };
    } catch {
      return { success: false };
    }
  };

  const PageContent = (
    <div className="pn-data-mapping">
      <div className="pn-data-mapping__intro">
        <span>Select sources to map, or</span>
        <CButton
          className="input-submit input-submit__primary input-submit__arrow"
          disabled={isAutoMapping || !canSaveWizards}
          title={canSaveWizards ? undefined : NO_PERMISSION_TITLE}
          onClick={e => {
            e.preventDefault();
            handleAutoMap();
          }}>
          {isAutoMapping ? (
            <>
              <Loader loading={true} />
              Auto-Mapping...
            </>
          ) : (
            "Auto-Map with TriloDocs"
          )}
        </CButton>
        {hasAutoMapped && (
          <StatusPill tone="positive">
            {`Auto-mapped · ${totalPatients} ${
              totalPatients === 1
                ? subjectsLabel.replace(/s$/, "")
                : subjectsLabel.toLowerCase()
            } · ${matchedCaseCount} CIOMS cases matched`}
          </StatusPill>
        )}
      </div>

      <div className="csr-file__card">
        <div className="pn-data-mapping__card-header">
          <div className="csr-file__card-title">Source listings</div>
          {linkedCsrId && (
            <span className="pn-data-mapping__card-context">
              from {DEMO_LINKED_CSR_LABEL} CSR project
            </span>
          )}
        </div>

        {SOURCE_LISTING_FIELDS.map(field => (
          <div className="pn-data-mapping__row" key={field.key}>
            <label className="pn-data-mapping__row-label">
              {field.label}
              {field.required ? (
                // Same marker the wizard questions use for a mandatory field.
                <span className="questions-content__required-asterisk"> *</span>
              ) : (
                <span className="pn-data-mapping__row-label--optional">
                  — optional
                </span>
              )}
            </label>
            <DataMappingSingleSelect
              loadOptions={loadOptionsFrom(SOURCE_LISTING_OPTIONS[field.key])}
              selectedValues={
                sourceListings[field.key] ? [sourceListings[field.key]] : []
              }
              onChange={values =>
                setSourceListings(prev => ({
                  ...prev,
                  [field.key]: values[0] || null,
                }))
              }
              placeholder={field.placeholder}
            />
          </div>
        ))}
      </div>

      <div className="csr-file__card">
        <div className="pn-data-mapping__card-header">
          <div className="csr-file__card-title">CIOMS source</div>
          {ciomsMeta && (
            <span className="pn-data-mapping__card-context">
              {`identifier field: ${ciomsMeta.identifierField} · ${ciomsMeta.followUpPolicy}`}
            </span>
          )}
        </div>

        <div className="pn-data-mapping__row">
          <DataMappingMultiSelect
            loadOptions={loadOptionsFrom(CIOMS_SOURCE_OPTIONS)}
            selectedValues={ciomsFiles}
            onChange={setCiomsFiles}
            placeholder="Select CIOMS source file(s)..."
          />
        </div>

        {ciomsMeta && (
          <div className="pn-data-mapping__summary">
            {`${ciomsMeta.caseCount} cases · ${ciomsMeta.subjectCount} subjects`}
          </div>
        )}
      </div>

      <div className="csr-file__card">
        <div className="pn-data-mapping__card-header">
          <div className="csr-file__card-title">{subjectsLabel} identified</div>
          <span className="pn-data-mapping__card-context">
            {`${totalPatients} total · ${fromListings} from listings · ${addedManually} added manually`}
          </span>
        </div>

        <div className="pn-data-mapping__patients-controls">
          <input
            type="text"
            className="pn-data-mapping__patient-input"
            placeholder="Add patient ID, e.g. 7931-0105-008"
            value={patientIdInput}
            onChange={e => setPatientIdInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddPatient();
              }
            }}
          />
          <button
            type="button"
            className="input-submit input-submit__tertiary input-submit__small"
            onClick={handleAddPatient}>
            Add
          </button>
          <button
            type="button"
            className="input-submit input-submit__tertiary input-submit__small"
            onClick={handleUploadListClick}>
            Upload list
          </button>
          <input
            type="file"
            accept=".csv,.txt"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleUploadListChange}
            data-testid="patient-list-file-input"
          />
        </div>

        <div className="pn-data-mapping__chip-list">
          {mappings.map(row => (
            <Chip
              key={row.patientId}
              onRemove={
                row.manual ? () => handleRemovePatient(row.patientId) : null
              }>
              {row.patientId}
            </Chip>
          ))}
        </div>
      </div>

      <div className="csr-file__card">
        <div className="pn-data-mapping__card-header">
          <div className="csr-file__card-title">Per-patient mapping</div>
          <span className="pn-data-mapping__card-context">
            Confirm CIOMS cases per patient
          </span>
        </div>

        <div className="pn-data-mapping__table-wrapper">
          <table className="pn-data-mapping__table">
            <thead>
              <tr>
                <th>Patient ID</th>
                <th>Reason for inclusion</th>
                <th>Listing events</th>
                <th>CIOMS cases</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {mappings.length === 0 && (
                <tr>
                  <td colSpan={5} className="pn-data-mapping__summary">
                    No {subjectsLabel.toLowerCase()} identified yet - Auto-Map
                    or add a patient ID above.
                  </td>
                </tr>
              )}
              {mappings.map(row => (
                <tr key={row.patientId}>
                  <td>{row.patientId}</td>
                  <td>
                    <div className="pn-data-mapping__reason-list">
                      {row.reasons.map(reason => (
                        <span key={reason} className="pn-data-mapping__reason">
                          {reason}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>{row.listingEvents}</td>
                  <td>
                    {row.ciomsCases.length > 0 ? (
                      <div className="pn-data-mapping__cioms-cell">
                        {row.ciomsCases.map(c => (
                          <React.Fragment key={c.caseId}>
                            <Chip>{c.caseId}</Chip>
                            {c.followUpLabel && (
                              <span className="pn-data-mapping__followup-badge">
                                {c.followUpLabel}
                              </span>
                            )}
                          </React.Fragment>
                        ))}
                        <span className="pn-data-mapping__summary">
                          {row.ciomsCases.reduce(
                            (sum, c) => sum + c.eventCount,
                            0
                          )}{" "}
                          events
                        </span>
                        <button
                          type="button"
                          className="pn-data-mapping__add-case-link"
                          onClick={() => handleAddCiomsCase(row.patientId)}>
                          + Add
                        </button>
                      </div>
                    ) : (
                      <DataMappingSingleSelect
                        className="pn-data-mapping__case-select"
                        loadOptions={loadOptionsFrom(CIOMS_CASE_OPTIONS)}
                        selectedValues={[]}
                        onChange={values =>
                          handleSelectCiomsCase(row.patientId, values[0])
                        }
                        placeholder="Select CIOMS case..."
                      />
                    )}
                  </td>
                  <td>
                    <StatusPill
                      tone={
                        row.status === "matched" ? "positive" : "attention"
                      }>
                      {row.status === "matched" ? "Matched" : "No CIOMS case"}
                    </StatusPill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={
        "Please complete the Data Mapping before proceeding to narrative generation"
      }
      headerButton={false}
      pageContent={PageContent}
      prevButton={{
        buttonTitle: "Narrative Format",
        buttonGoToAction: "/pn/" + patientNarrativeId + "/narrative-format",
      }}
      nextButton={{
        buttonTitle: "Generate Narratives",
        buttonGoToAction: "/pn/" + patientNarrativeId + "/file",
        disabled: !hasAutoMapped,
      }}
      saveButton={true}
      savePermission={[PERMISSIONS.pnWizardsRead, PERMISSIONS.pnWizardsUpdate]}
      saveButtonHandler={handleSave}
    />
  );
};

export default DataMapping;
